import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Search } from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { useUtilityServices } from "@features/cx/consumerOnboarding/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useGetResourceTypeList,
  useGetResourceSubtypeList,
} from "@features/mx/hooks";
import {
  useGetAvailableResources,
  useGetResourceUnitAssignments,
} from "@features/onboarding/utility-setup/hooks";
import {
  InventoryResource,
  ActiveResource,
  ModalMode,
} from "./AddResourceModal";

interface ResourceSelectionStepProps {
  mode: ModalMode;
  activeResources: ActiveResource[];
  replacingResource: ActiveResource | null;
  onSelectReplacingResource: (resource: ActiveResource | null) => void;
  onSelectNewResource: (resource: InventoryResource) => void;
}

const getServiceDotColor = (service: string) => {
  switch (service) {
    case "Water":
      return "bg-blue-500";
    case "Electricity":
      return "bg-amber-500";
    case "Gas":
      return "bg-orange-500";
    case "Solar":
      return "bg-emerald-500";
    case "Hot Water":
      return "bg-red-500";
    case "Waste Water":
      return "bg-cyan-500";
    case "Broadband":
      return "bg-purple-500";
    case "EV":
      return "bg-emerald-500";
    default:
      return "bg-gray-400";
  }
};

const getServiceIconBg = (service: string) => {
  switch (service) {
    case "Water":
      return "bg-blue-50 border-blue-100";
    case "Electricity":
      return "bg-amber-50 border-amber-100";
    case "Gas":
      return "bg-orange-50 border-orange-100";
    case "Solar":
      return "bg-emerald-50 border-emerald-100";
    case "Hot Water":
      return "bg-red-50 border-red-100";
    case "Waste Water":
      return "bg-cyan-50 border-cyan-100";
    case "Broadband":
      return "bg-purple-50 border-purple-100";
    case "EV":
      return "bg-emerald-50 border-emerald-100";
    default:
      return "bg-gray-50 border-gray-100";
  }
};

export const ResourceSelectionStep: React.FC<ResourceSelectionStepProps> = ({
  mode,
  activeResources,
  replacingResource,
  onSelectReplacingResource,
  onSelectNewResource,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const [searchQuery, setSearchQuery] = useState("");
  const { data: utilityData } = useUtilityServices();

  const form = useForm({
    defaultValues: {
      utility_service: "",
      resource_type: "",
      sub_type: "",
    },
  });

  const selectedService = form.watch("utility_service") || "";
  const selectedResourceType = form.watch("resource_type") || "";
  const selectedSubType = form.watch("sub_type") || "";

  const { data: resourceTypeResponse } =
    useGetResourceTypeList(remoteUtilityId);
  const { data: subtypesResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    selectedResourceType ? [selectedResourceType] : undefined,
  );

  const { code } = useParams<{ code?: string }>();
  const unitCode = decodeURIComponent(code || "");

  const { data: assignmentsData, isLoading: isAssignmentsLoading } =
    useGetResourceUnitAssignments({
      remoteUtilityId,
      unitCode,
    });

  const { data: availableResourcesData, isLoading: isAvailableLoading } =
    useGetAvailableResources({
      remoteUtilityId,
      utilityService: selectedService || undefined,
      resourceTypeCode: selectedResourceType || undefined,
      resourceSubtypeCode: selectedSubType || undefined,
    });

  const availableInventoryList: InventoryResource[] = useMemo(() => {
    const rawList = Array.isArray(availableResourcesData)
      ? availableResourcesData
      : Array.isArray(availableResourcesData?.results)
        ? availableResourcesData.results
        : Array.isArray(availableResourcesData?.result)
          ? availableResourcesData.result
          : Array.isArray(availableResourcesData?.data)
            ? availableResourcesData.data
            : [];

    return rawList.map(
      (item: any, index: number): InventoryResource => ({
        id: String(item.id ?? index + 1),
        resourceId:
          item.resource_id ||
          item.resourceId ||
          item.meter_no ||
          item.meterNumber ||
          item.code ||
          "N/A",
        service:
          item.utility_service ||
          item.utilityService ||
          item.service ||
          selectedService ||
          "N/A",
        type: item.resourceTypeName || "N/A",
        subType: item.resourceSubTypeName || item.subType || "N/A",
        serialNo: item.serialNo || "N/A",
        manufacturer: item.manufacturer || item.manufacturerName || "N/A",
        model: item.model || item.modelName || "N/A",
        capacity: item.capacity || item.capacityUnit || "0",
        location:
          item.location || item.warehouse || item.warehouseName || "N/A",
        status: item.status || "N/A",
      }),
    );
  }, [availableResourcesData, selectedService]);

  const resourceTypeOptions = useMemo(() => {
    const results = resourceTypeResponse?.result || resourceTypeResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
          label: item.name,
          value: item.code,
        }))
      : [];
  }, [resourceTypeResponse]);

  const subTypeOptions = useMemo(() => {
    const results = subtypesResponse?.result || subtypesResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
          label: item.name,
          value: item.code,
        }))
      : [];
  }, [subtypesResponse]);

  const getUtilityOptions = useMemo(
    () =>
      utilityData
        ? utilityData
            .filter((utility: any) => utility.isActive === true)
            .map((utility: any) => ({
              value: utility.name,
              label: utility.name,
            }))
        : [],
    [utilityData],
  );

  // Reset dependent form fields when utility_service changes
  const handleServiceChange = (service: string) => {
    form.setValue("resource_type", "");
    form.setValue("sub_type", "");
    setSearchQuery("");
  };

  // Reset sub_type when resource_type changes
  const handleResourceTypeChange = (type: string) => {
    form.setValue("sub_type", "");
    setSearchQuery("");
  };

  // Filter available inventory resources
  const filteredInventory = useMemo(() => {
    if (!selectedService) return [];

    return availableInventoryList.filter((item) => {
      // Match Search query across ID, serial, model, manufacturer
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesSearch =
          item.resourceId.toLowerCase().includes(q) ||
          item.serialNo.toLowerCase().includes(q) ||
          item.model.toLowerCase().includes(q) ||
          item.manufacturer.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [selectedService, availableInventoryList, searchQuery]);

  const assignedResourcesList: ActiveResource[] = useMemo(() => {
    const rawList = Array.isArray(assignmentsData?.result)
      ? assignmentsData.result
      : Array.isArray(assignmentsData)
        ? assignmentsData
        : activeResources || [];

    if (rawList && rawList.length > 0) {
      return rawList.map(
        (item: any, index: number): ActiveResource => ({
          id: item.meterId,
          meterId: item.resourceId || "N/A",
          service: item.utilityService || "N/A",
          serialNo: item.serialNo || "N/A",
          type: item.resourceTypeName || "N/A",
          subType: item.resourceSubTypeName || "N/A",
          lastReading: item.lastReading || "0 gal",
          readDate: item.readDate || "N/A",
          installDate: item.installDate || "N/A",
        }),
      );
    }

    return activeResources || [];
  }, [assignmentsData, activeResources]);

  // If in Update Existing mode and no replacing resource has been chosen yet:
  if (mode === "update_existing" && !replacingResource) {
    return (
      <div className="py-2 space-y-4">
        <p className="text-xs font-medium text-gray-600">
          Select the resource you want to update:
        </p>

        <div className="space-y-2.5 max-h-[380px] overflow-y-auto pr-1">
          {isAssignmentsLoading ? (
            <div className="py-8 text-center text-xs text-gray-500 font-medium bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
              Loading assigned resources...
            </div>
          ) : assignedResourcesList.length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-500 font-medium bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
              No assigned resources found to update
            </div>
          ) : (
            assignedResourcesList.map((resource) => (
              <div
                key={resource.id}
                onClick={() => onSelectReplacingResource(resource)}
                className="group flex items-center justify-between p-3.5 rounded-xl border border-gray-200 hover:border-amber-400 hover:bg-amber-50/20 cursor-pointer transition-all duration-150 shadow-2xs"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${getServiceIconBg(
                      resource.service,
                    )}`}
                  >
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${getServiceDotColor(
                        resource.service,
                      )}`}
                    />
                  </div>
                  <div className="space-y-0.5">
                    <h5 className="text-xs font-bold text-gray-900 group-hover:text-amber-800">
                      {resource.meterId}
                    </h5>
                    <p className="text-[11px] text-gray-500">
                      {[
                        resource.service !== "N/A" ? resource.service : null,
                        resource.type !== "N/A" ? resource.type : null,
                        resource.serialNo !== "N/A" ? resource.serialNo : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold text-gray-600">
                    Last: {resource.lastReading}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Cascading Form Fields configuration for DynamicForm
  const formFields: FormField[] = [
    {
      name: "utility_service",
      label: "Utility Service",
      type: "select",
      placeholder: "Select...",
      options: getUtilityOptions,
      className: "col-span-1",
    },
    {
      name: "resource_type",
      label: "Resource Type",
      type: "select",
      placeholder: "Select...",
      options: resourceTypeOptions,
      disabled: !selectedService,
      className: "col-span-1",
    },
    {
      name: "sub_type",
      label: "Sub Type",
      type: "select",
      placeholder: "Select...",
      options: subTypeOptions,
      disabled: !selectedResourceType,
      className: "col-span-1",
    },
    {
      name: "inventoryListCustom",
      type: "custom",
      className: "col-span-3",
      customRender: () => {
        if (!selectedService) {
          return (
            <div className="py-12 text-center text-xs text-gray-400 font-medium">
              Select a utility service above to browse available resources
            </div>
          );
        }

        return (
          <div className="space-y-3 pt-1">
            {/* Real-time Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by ID, serial, model or manufacturer…"
                className="w-full h-9 pl-9 pr-3 text-xs border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white placeholder:text-gray-400"
              />
            </div>

            {/* Inventory List Cards */}
            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
              {isAvailableLoading ? (
                <div className="py-8 text-center text-xs text-gray-500 font-medium bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
                  Loading available resources...
                </div>
              ) : filteredInventory.length === 0 ? (
                <div className="py-8 text-center text-xs text-gray-500 font-medium bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
                  No resources found matching your criteria
                </div>
              ) : (
                filteredInventory.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => onSelectNewResource(item)}
                    className="group flex items-center justify-between p-3 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50/30 cursor-pointer transition-all duration-150 shadow-2xs"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${getServiceIconBg(
                          item.service,
                        )}`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${getServiceDotColor(
                            item.service,
                          )}`}
                        />
                      </div>
                      <div className="space-y-0.5">
                        <h5 className="text-xs font-bold text-gray-900 group-hover:text-blue-700">
                          {item.resourceId}
                        </h5>
                        <p className="text-[11px] text-gray-500">
                          {[
                            item.serialNo !== "N/A" ? item.serialNo : null,
                            item.subType !== "N/A" ? item.subType : null,
                            item.location !== "N/A" ? item.location : null,
                            item.manufacturer !== "N/A"
                              ? item.manufacturer
                              : null,
                            item.model !== "N/A" ? item.model : null,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="shrink-0">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/80 inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Available
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="py-2 space-y-4">
      {/* Replacing Chip if in Update Existing mode */}
      {mode === "update_existing" && replacingResource && (
        <div className="space-y-1">
          <span className="block text-xs font-semibold text-gray-600">
            Replacing
          </span>
          <div className="p-3 bg-amber-50/80 border border-amber-300 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${getServiceIconBg(
                  replacingResource.service,
                )}`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${getServiceDotColor(
                    replacingResource.service,
                  )}`}
                />
              </div>
              <div className="space-y-0.5">
                <h5 className="text-xs font-bold text-amber-900">
                  {replacingResource.meterId}
                </h5>
                <p className="text-[11px] text-amber-700">
                  {replacingResource.service} · {replacingResource.type} ·{" "}
                  {replacingResource.serialNo}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                onSelectReplacingResource(null);
                form.reset({
                  utility_service: "",
                  resource_type: "",
                  sub_type: "",
                });
                setSearchQuery("");
              }}
              className="text-xs font-semibold text-amber-800 hover:text-amber-950 underline underline-offset-2 cursor-pointer"
            >
              Change
            </button>
          </div>
        </div>
      )}

      {/* DynamicForm rendering the 3 cascading select dropdowns & custom inventory list */}
      <div className="space-y-2">
        {mode === "update_existing" && (
          <span className="block text-xs font-semibold text-gray-600">
            Select New Resource
          </span>
        )}

        <DynamicForm
          fields={formFields}
          form={form}
          gridColumns={3}
          responsiveColumns={{ sm: 1, md: 3 }}
          onFieldChange={(fieldName, value) => {
            if (fieldName === "utility_service") {
              handleServiceChange(value);
            } else if (fieldName === "resource_type") {
              handleResourceTypeChange(value);
            }
          }}
          config={{
            showResetButton: false,
            submitButtonText: "",
            showProgress: false,
            classes: BUTTON_FIELD_CLASSES,
          }}
        />
      </div>
    </div>
  );
};
